import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BlockIcon from "@mui/icons-material/Block";

import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

// Define configuration options for Auto Pilot
const configOptions = [
  {
    id: "enable_auto_pilot",
    title: "Piloto Automático",
    description: {
      enabled:
        "O Auto Piloto está ativado, permitindo que o sistema processe pedidos automaticamente sem que você tenha um dispositivo conectado no Slapie",
      disabled:
        "O Auto Piloto está desativado, você precisará ligar algum dispositivo no Slapie para seus pedidos serem aceitos",
    },
    icon: AutoFixHighIcon,
    successMessage: (value) =>
      `Auto Piloto ${value ? "ativado" : "desativado"} com sucesso`,
    errorMessage: "Erro ao atualizar configuração do Piloto Automático",
    notificationId: "auto-pilot-config",
  },
];

// Component for displaying a single config item
const ConfigItem = ({ config, isChecked, onChange, theme }) => {
  const ConfigIcon = config.icon;

  return (
    <Paper
      elevation={3}
      sx={{
        mb: 3,
        overflow: "hidden",
        borderRadius: 2,
        transition: "all 0.3s ease",
        border: "1px solid",
        borderColor: isChecked
          ? alpha(theme.palette.success.main, 0.3)
          : alpha(theme.palette.warning.main, 0.3),
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background gradient */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: isChecked
              ? `linear-gradient(45deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
              : `linear-gradient(45deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(
                  isChecked
                    ? theme.palette.success.main
                    : theme.palette.warning.main,
                  0.1
                ),
                color: isChecked
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                borderRadius: 1.5,
                p: 0.75,
                width: 36,
                height: 36,
                mr: 2,
              }}
            >
              {isChecked ? (
                <ConfigIcon fontSize="small" />
              ) : (
                <BlockIcon fontSize="small" />
              )}
            </Box>

            <Typography variant="h6" fontWeight={500}>
              {config.title}
            </Typography>

            <Box sx={{ ml: "auto" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isChecked}
                    onChange={(e) => onChange(config.id, e)}
                    color={isChecked ? "success" : "warning"}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: isChecked
                          ? theme.palette.success.main
                          : theme.palette.warning.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: isChecked
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                        },
                    }}
                  />
                }
                label=""
              />
            </Box>
          </Box>

          <Box
            sx={{
              ml: 7,
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={500}
              color={isChecked ? "success.main" : "warning.main"}
            >
              {isChecked ? "Ativado" : "Desativado"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isChecked
                ? config.description.enabled
                : config.description.disabled}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// Hook to manage config state and API interactions
const useAutoPilotConfig = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});

  const loadConfigs = async () => {
    try {
      setLoading(true);

      // Create config params string from configOptions
      const configParams = configOptions.map((option) => option.id).join(",");

      const params = {
        config: configParams,
      };

      const { data } = await getDeliveryCenterConfig(params);

      // Update state with config values
      setConfigValues(data);
    } catch (err) {
      console.log(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configurações. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "configs-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    try {
      const newValue = event.target.checked;
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      if (!configOption) return;

      const data = {
        config: {
          [configId]: newValue,
        },
      };

      await setDeliveryCenterConfig(data);

      // Update local state
      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValue,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        configOption.successMessage(newValue),
        "success"
      );
    } catch (e) {
      console.error(e);
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      dispatchSnackBarMessage(
        `${configId}-update-error`,
        configOption?.errorMessage || "Erro ao atualizar configuração",
        "error"
      );
    }
  };

  return {
    loading,
    errorMessage,
    showError,
    configValues,
    loadConfigs,
    handleConfigChange,
    setShowError,
  };
};

// Main component
export default function AutoPilotConfigModal({ show, closeModal }) {
  const theme = useTheme();
  const {
    loading,
    errorMessage,
    showError,
    configValues,
    loadConfigs,
    handleConfigChange,
    setShowError,
  } = useAutoPilotConfig();

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  const renderConfigItems = () => {
    return configOptions.map((config) => (
      <ConfigItem
        key={config.id}
        config={config}
        isChecked={configValues[config.id] || false}
        onChange={handleConfigChange}
        theme={theme}
      />
    ));
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Configurações do Auto Piloto"
        icon={AutoFixHighIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderConfigItems()
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
